import { LINK_FONTS } from './links.js';
import { z } from 'zod';

/*
 * Storefront design (ADR-0128): the header, navigation bar, side menu, footer and the sections
 * of the three designed pages (home, products, categories). The merchant edits a draft in the
 * console and publishes it; the storefront renders the published design, or the legacy layout
 * while nothing was published. The same schema validates what the API stores and what the
 * storefront reads, so a design is never half-understood.
 */

const DESIGN_TEMPLATES = ['retail', 'restaurant'] as const;
export type DesignTemplate = (typeof DESIGN_TEMPLATES)[number];
export const DESIGN_PAGES = ['home', 'products', 'categories', 'product', 'cart'] as const;
export type DesignPage = (typeof DESIGN_PAGES)[number];

export const SECTION_TYPES = [
  'slider',
  'banner',
  'products',
  'categories',
  'image',
  'video',
  'text',
  'features',
  'menu',
  'info',
  'catalog',
  'countdown',
  'testimonials',
  'faq',
  'gallery',
  'logos',
  'collage',
  'cta',
  'spacer',
  // ADR-0128 amendment 7: layout and motion sections.
  'divider',
  'columns',
  'split',
  'cards',
  'tabs',
  'marquee',
  'stats',
  'steps',
] as const;
export type SectionType = (typeof SECTION_TYPES)[number];

const text = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((v) => v || null);

/** A text in both languages; the storefront falls back to the other one when one is empty. */
const localized = (max: number) =>
  z.object({ en: text(max), ar: text(max) }).default({ en: null, ar: null });
export type Localized = { en: string | null; ar: string | null };

const id = z
  .string()
  .trim()
  .min(1)
  .max(40)
  .regex(/^[A-Za-z0-9_-]+$/);
const uuidLike = z.string().trim().min(1).max(64);

/** Outside links: https only (or a phone / e-mail); inside links start with one slash. */
function isSafeLinkUrl(value: string): boolean {
  if (/^https:\/\/[^\s]+$/i.test(value)) return true;
  if (/^(tel:\+?[0-9 -]{3,20}|mailto:[^\s@]+@[^\s@]+)$/i.test(value)) return true;
  return /^\/(?!\/)[^\s]*$/.test(value);
}

const designLinkSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('page'),
    page: z.enum(['home', 'products', 'categories', 'cart', 'account', 'brands', 'wishlist']),
  }),
  z.object({ kind: z.literal('category'), categoryId: uuidLike }),
  z.object({ kind: z.literal('product'), productId: uuidLike }),
  z.object({
    kind: z.literal('url'),
    url: z
      .string()
      .trim()
      .max(500)
      .refine(isSafeLinkUrl, 'Use an https:// address, a phone, an e-mail or a store path.'),
  }),
]);
export type DesignLink = z.infer<typeof designLinkSchema>;
const link = designLinkSchema.nullish().transform((v) => v ?? null);

/** An uploaded design image (`online_store_assets.id`), or null. */
const image = z
  .uuid()
  .nullish()
  .transform((v) => v ?? null);

/** The embed address of a YouTube, Vimeo or TikTok video, or null for anything else. */
export function videoEmbed(url: string): { provider: string; src: string } | null {
  const m = /^https:\/\/([^/?#]+)([^?#]*)(\?[^#]*)?/i.exec(url.trim());
  if (!m) return null;
  const host = m[1]!.toLowerCase().replace(/^(www\.|m\.)/, '');
  const parts = m[2]!.split('/').filter(Boolean);
  const query = new Map(
    (m[3] ?? '')
      .slice(1)
      .split('&')
      .map((kv) => kv.split('=') as [string, string]),
  );
  const clean = (v: string | undefined | null) => (v && /^[A-Za-z0-9_-]{4,40}$/.test(v) ? v : null);
  if (host === 'youtube.com' || host === 'youtu.be' || host === 'youtube-nocookie.com') {
    const vid = clean(
      host === 'youtu.be'
        ? parts[0]
        : parts[0] === 'shorts' || parts[0] === 'embed' || parts[0] === 'live'
          ? parts[1]
          : query.get('v'),
    );
    return vid
      ? { provider: 'youtube', src: `https://www.youtube-nocookie.com/embed/${vid}` }
      : null;
  }
  if (host === 'vimeo.com' || host === 'player.vimeo.com') {
    const vid = parts.find((p) => /^[0-9]{3,15}$/.test(p));
    return vid ? { provider: 'vimeo', src: `https://player.vimeo.com/video/${vid}` } : null;
  }
  if (host === 'tiktok.com') {
    const at = parts.indexOf('video');
    const vid = at >= 0 ? parts[at + 1] : undefined;
    return vid && /^[0-9]{5,25}$/.test(vid)
      ? { provider: 'tiktok', src: `https://www.tiktok.com/embed/v2/${vid}` }
      : null;
  }
  return null;
}

// ------------------------------------------------------------------------------- sections
/** A moment in time (ISO 8601 with an offset), or null. */
const moment = z
  .string()
  .trim()
  .max(40)
  .refine((v) => !Number.isNaN(Date.parse(v)), 'Use a valid date and time.')
  .nullish()
  .transform((v) => v ?? null);

/** A colour as `#rrggbb`, or null. */
const hexColor = z
  .string()
  .trim()
  .regex(/^#[0-9a-fA-F]{6}$/, 'Use a colour like #1a2b3c.')
  .nullish()
  .transform((v) => v ?? null);

/** Ready gradients for a section background (ADR-0128 amendment 7); `brand` follows the store colour. */
export const SECTION_GRADIENTS = [
  'brand',
  'sunset',
  'ocean',
  'candy',
  'mint',
  'night',
  'peach',
  'aurora',
] as const;
/** Shapes drawn on the top or bottom edge of a coloured section. */
export const SHAPE_DIVIDERS = ['none', 'wave', 'curve', 'slant', 'zigzag', 'peaks'] as const;
/**
 * How a section appears when it scrolls into view: `none` follows the page's motion
 * (`style.motion`), `off` never moves.
 */
export const SECTION_ANIMATIONS = ['none', 'fade', 'up', 'zoom', 'slide', 'off'] as const;

/**
 * How any section sits on the page (ADR-0128 amendments 3 and 7): its background (colours,
 * gradients, a custom colour or a picture), spacing, width (narrow, contained, edge to edge),
 * height (up to the full screen), a boxed card, rounded corners, edge shapes, heading (kicker,
 * size, subtitle, alignment), motion on scroll, a direct-link anchor, sticky / overlapping /
 * collapsible behaviour, the screens it shows on, and when it shows.
 */
const sectionLookSchema = z
  .object({
    background: z
      .enum(['none', 'soft', 'tinted', 'brand', 'dark', 'gradient', 'image', 'custom'])
      .default('none'),
    gradient: z.enum(SECTION_GRADIENTS).default('brand'),
    /** The `custom` background. */
    bgColor: hexColor,
    /** The `image` background, with a shade so the words stay readable. */
    bgImageId: image,
    bgOverlay: z.enum(['none', 'light', 'dark']).default('dark'),
    spacing: z.enum(['none', 'compact', 'regular', 'roomy', 'huge']).default('regular'),
    /** `narrow` = reading width, `full` = edge to edge; a coloured background always spans the screen. */
    width: z.enum(['narrow', 'contained', 'full']).default('contained'),
    /** `screen` = at least the height of the screen, content in the middle. */
    height: z.enum(['auto', 'half', 'screen']).default('auto'),
    /** The content sits on a card inside the section (a boxed section). */
    boxed: z.boolean().default(false),
    /** Rounded background when the section does not span the screen. */
    corners: z.enum(['none', 'soft', 'round']).default('none'),
    dividerTop: z.enum(SHAPE_DIVIDERS).default('none'),
    dividerBottom: z.enum(SHAPE_DIVIDERS).default('none'),
    titleAlign: z.enum(['start', 'center']).default('start'),
    titleSize: z.enum(['small', 'regular', 'large', 'huge']).default('regular'),
    /** The heading in the text colour or the store colour. */
    titleColor: z.enum(['default', 'brand']).default('default'),
    /** A small line above the heading. */
    eyebrow: localized(60),
    /** The heading of a section without a title of its own (`ownTitle` says which have one). */
    heading: localized(120),
    subtitle: localized(200),
    animation: z.enum(SECTION_ANIMATIONS).default('none'),
    /** A direct link to the section: `/#anchor` in a button or menu item. */
    anchor: z
      .string()
      .trim()
      .max(40)
      .regex(/^[a-z0-9-]*$/, 'Lowercase letters, digits and dashes only.')
      .default(''),
    /** Stays at the top while the page scrolls past it. */
    sticky: z.boolean().default(false),
    /** Pulled up over the section before it. */
    overlap: z.boolean().default(false),
    /** Folded behind its label until the shopper opens it. */
    collapsible: z.boolean().default(false),
    collapseLabel: localized(80),
    devices: z.enum(['all', 'desktop', 'phone']).default('all'),
    showFrom: moment,
    showUntil: moment,
  })
  .refine((l) => !l.showFrom || !l.showUntil || Date.parse(l.showFrom) < Date.parse(l.showUntil), {
    message: 'The section must start before it ends.',
    path: ['showUntil'],
  });
export type SectionLook = z.output<typeof sectionLookSchema>;

const base = { id, visible: z.boolean().default(true), look: sectionLookSchema.prefault({}) };

const slideSchema = z.object({
  id,
  imageId: image,
  /** Another picture for phones (ADR-0146): a square one reads better there. */
  mobileImageId: image,
  title: localized(80),
  subtitle: localized(160),
  button: localized(30),
  link,
});
const sliderSectionSchema = z.object({
  ...base,
  type: z.literal('slider'),
  variant: z.enum(['full', 'contained', 'cards']).default('contained'),
  autoplay: z.boolean().default(true),
  /** Seconds per slide. */
  interval: z.union([z.literal(3), z.literal(5), z.literal(8)]).default(5),
  /** `banner` = a 3:1 banner on large screens and a square on phones (ADR-0146). */
  height: z.enum(['small', 'medium', 'large', 'banner']).default('medium'),
  overlay: z.enum(['none', 'light', 'dark']).default('dark'),
  textAlign: z.enum(['start', 'center']).default('start'),
  showArrows: z.boolean().default(true),
  slides: z.array(slideSchema).max(8).default([]),
});
const bannerSectionSchema = z.object({
  ...base,
  type: z.literal('banner'),
  /** `card` / `glass` = the words on a card over the picture. */
  variant: z.enum(['overlay', 'split', 'color', 'card', 'glass']).default('overlay'),
  /** `store` = the store's banner picture (settings), `upload` = `imageId`. */
  imageSource: z.enum(['store', 'upload', 'none']).default('upload'),
  imageId: image,
  title: localized(80),
  subtitle: localized(200),
  button: localized(30),
  link,
  /** A second, quieter button. */
  button2: localized(30),
  link2: link,
  align: z.enum(['start', 'center', 'end']).default('start'),
  height: z.enum(['small', 'medium', 'large', 'screen']).default('medium'),
  overlay: z.enum(['none', 'light', 'medium', 'dark', 'gradient']).default('dark'),
  // ADR-0128 amendment 7.
  /** Where the words sit over the picture. */
  position: z.enum(['top', 'center', 'bottom']).default('bottom'),
  /** A small line above the title. */
  eyebrow: localized(60),
  titleSize: z.enum(['regular', 'large', 'huge']).default('regular'),
  buttonStyle: z.enum(['solid', 'outline', 'pill', 'glass']).default('solid'),
  /** Another picture for phones (tall pictures read better there). */
  mobileImageId: image,
  /** A slow zoom on the picture. */
  zoom: z.boolean().default(false),
  rounded: z.enum(['none', 'soft', 'round']).default('soft'),
  /** `full` = from one side of the screen to the other, without corners. */
  width: z.enum(['contained', 'full']).default('contained'),
});
/** How product cards look in a list (ADR-0128 amendment 7). */
const cardOptions = {
  /** `divided` = the cards share one frame with lines between them. */
  cardStyle: z
    .enum(['default', 'minimal', 'bordered', 'soft', 'overlay', 'divided'])
    .default('default'),
  aspect: z.enum(['square', 'portrait', 'landscape']).default('square'),
  mobileColumns: z.union([z.literal(1), z.literal(2)]).default(2),
  /** The second picture shows when the pointer is on the card. */
  hoverImage: z.boolean().default(false),
  /** `contain` shows the whole picture on white (products photographed on white). */
  imageFit: z.enum(['cover', 'contain']).default('cover'),
  /** The product's category as a small line above its name. */
  showCategory: z.boolean().default(false),
  /** The name in full, or cut after one or two lines. */
  nameLines: z.enum(['full', '1', '2']).default('full'),
  priceSize: z.enum(['regular', 'large']).default('regular'),
  textAlign: z.enum(['start', 'center']).default('start'),
  /** A buy button: none, small beside the price, or across the card's width. */
  buyButton: z.enum(['none', 'inline', 'full']).default('none'),
  /** Its words (empty = "Add to cart"); products with options open their page. */
  buyLabel: localized(30),
  /** Its colour (empty = the store colour); the words take the readable colour. */
  buyColor: hexColor,
  /** Sold out as words under the price, or a small badge on the picture's corner. */
  soldOut: z.enum(['text', 'corner']).default('text'),
  /** A "New" badge on products added in the last N days; 0 = none (ADR-0146). */
  newBadgeDays: z.number().int().min(0).max(90).default(0),
  /** A heart to keep the product in the shopper's favourites (ADR-0146). */
  wishlist: z.boolean().default(false),
  /**
   * The product's roast / production date on the picture (ADR-0147): `date` = the date alone,
   * `roast` / `production` = with that word; `off` = not shown.
   */
  dateBadge: z.enum(['off', 'date', 'roast', 'production']).default('off'),
  /** The date badge's colour (empty = red). */
  dateBadgeColor: hexColor,
};
/** Where a products section takes its products from (`newest` = latest added, ADR-0146). */
export const PRODUCT_SOURCES = [
  'featured',
  'all',
  'category',
  'manual',
  'bestSellers',
  'newest',
] as const;
const productsSectionSchema = z.object({
  ...base,
  type: z.literal('products'),
  variant: z.enum(['grid', 'carousel', 'list']).default('grid'),
  title: localized(80),
  /** `bestSellers`: the most sold first (`soldRank`); `limit` is their most. */
  source: z.enum(PRODUCT_SOURCES).default('featured'),
  /**
   * Tabs over the products (ADR-0146), e.g. best sellers / newest: the shopper switches
   * between these sources; empty = no tabs, `source` alone.
   */
  tabs: z
    .array(z.enum(['featured', 'all', 'bestSellers', 'newest']))
    .max(4)
    .default([]),
  /** A "Load more" button under the products: `limit` more each time (ADR-0146). */
  loadMore: z.boolean().default(false),
  categoryId: uuidLike.nullish().transform((v) => v ?? null),
  productIds: z.array(uuidLike).max(24).default([]),
  limit: z.number().int().min(1).max(24).default(8),
  columns: z.number().int().min(2).max(6).default(4),
  sort: z.enum(['position', 'name', 'priceAsc', 'priceDesc']).default('position'),
  /** One-tap "add" on products with a single option. */
  showAdd: z.boolean().default(false),
  showViewAll: z.boolean().default(true),
  ...cardOptions,
});
const categoriesSectionSchema = z.object({
  ...base,
  type: z.literal('categories'),
  variant: z
    .enum(['tiles', 'circles', 'chips', 'list', 'cards', 'banners', 'carousel'])
    .default('tiles'),
  title: localized(80),
  /** Empty = every category of the store, in its order. */
  categoryIds: z.array(uuidLike).max(40).default([]),
  columns: z.number().int().min(2).max(6).default(4),
  /** Columns on phones (tiles and cards). */
  mobileColumns: z.union([z.literal(1), z.literal(2), z.literal(3)]).default(2),
  /** Picture shape of tiles and cards (ADR-0128 amendment 6). */
  aspect: z.enum(['landscape', 'square', 'portrait']).default('landscape'),
  /** How many products each category holds. */
  showCount: z.boolean().default(false),
  /** Only the main categories (when none is chosen by hand). */
  topOnly: z.boolean().default(false),
  /** Each category's subcategories as small links under it (tiles, cards, list). */
  showSub: z.boolean().default(false),
  showViewAll: z.boolean().default(false),
  // ADR-0128 amendment 7.
  /** The name under the picture or over it (tiles, carousel). */
  labelPosition: z.enum(['overlay', 'below']).default('overlay'),
  hover: z.enum(['none', 'zoom', 'lift']).default('zoom'),
});
const imageSectionSchema = z.object({
  ...base,
  type: z.literal('image'),
  variant: z.enum(['contained', 'full']).default('contained'),
  imageId: image,
  alt: localized(120),
  link,
  aspect: z.enum(['wide', 'square', 'tall', 'auto']).default('wide'),
});
const videoSectionSchema = z.object({
  ...base,
  type: z.literal('video'),
  variant: z.enum(['contained', 'full']).default('contained'),
  title: localized(80),
  url: z
    .string()
    .trim()
    .max(300)
    .refine((v) => v === '' || videoEmbed(v) !== null, 'Use a YouTube, Vimeo or TikTok link.')
    .default(''),
  aspect: z.enum(['landscape', 'portrait']).default('landscape'),
});
const textSectionSchema = z.object({
  ...base,
  type: z.literal('text'),
  variant: z.enum(['plain', 'card', 'highlight']).default('plain'),
  title: localized(120),
  body: localized(2000),
  align: z.enum(['start', 'center']).default('start'),
  button: localized(30),
  link,
});
const featuresSectionSchema = z.object({
  ...base,
  type: z.literal('features'),
  variant: z.enum(['row', 'grid', 'cards', 'list']).default('row'),
  title: localized(80),
  items: z
    .array(
      z.object({
        id,
        icon: z.string().trim().max(8).default(''),
        title: localized(60),
        text: localized(160),
        link,
      }),
    )
    .max(6)
    .default([]),
  // ADR-0128 amendment 7.
  iconStyle: z.enum(['circle', 'plain', 'square', 'outline']).default('circle'),
  iconSize: z.enum(['small', 'regular', 'large']).default('regular'),
  /** `auto` = centred in the grid, beside the icon otherwise. */
  align: z.enum(['auto', 'start', 'center']).default('auto'),
  /** Columns of the grid and cards (0 = as many as fit). */
  columns: z.union([z.literal(0), z.literal(2), z.literal(3), z.literal(4)]).default(0),
});
/** The restaurant menu: products grouped by category with jump tabs. */
const menuSectionSchema = z.object({
  ...base,
  type: z.literal('menu'),
  /** `delivery`: rows like a delivery app (words first, the picture at the end). */
  variant: z.enum(['list', 'grid', 'compact', 'delivery']).default('list'),
  title: localized(80),
  stickyTabs: z.boolean().default(true),
  showImages: z.boolean().default(true),
  showDescriptions: z.boolean().default(true),
  categoryIds: z.array(uuidLike).max(40).default([]),
  /**
   * Quick add (like delivery apps): a round + on the picture adds one at once and opens − n +;
   * after a moment it folds into a bubble with the quantity.
   */
  quickAdd: z.boolean().default(false),
  /**
   * A cart bar at the bottom of the screen while something is in the cart: count, "View cart",
   * total, and how much is left to the minimum order or to free delivery.
   */
  cartBar: z.boolean().default(false),
});
/** Opening state, preparation / delivery times, minimum order and branches. */
const infoSectionSchema = z.object({
  ...base,
  type: z.literal('info'),
  variant: z.enum(['strip', 'cards']).default('strip'),
  showHours: z.boolean().default(true),
  showDelivery: z.boolean().default(true),
  showBranches: z.boolean().default(false),
  showContact: z.boolean().default(false),
});
/** The browsable product list with search, sort and category filter. */
const catalogSectionSchema = z.object({
  ...base,
  type: z.literal('catalog'),
  variant: z.enum(['grid', 'list']).default('grid'),
  showSearch: z.boolean().default(true),
  showSort: z.boolean().default(true),
  showCategories: z.boolean().default(true),
  showFeatured: z.boolean().default(true),
  columns: z.number().int().min(2).max(6).default(4),
  // ADR-0128 amendment 7.
  ...cardOptions,
  showAdd: z.boolean().default(false),
  /** Category buttons as chips, tabs or a drop-down list. */
  filterStyle: z.enum(['chips', 'tabs', 'dropdown']).default('chips'),
  /** The search and filters stay at the top while scrolling. */
  stickyFilters: z.boolean().default(false),
  /** Products per page with a "show more" button; 0 = all at once. */
  pageSize: z.union([z.literal(0), z.literal(12), z.literal(24), z.literal(48)]).default(0),
  defaultSort: z.enum(['default', 'name', 'priceAsc', 'priceDesc']).default('default'),
  /** "24 products" above the list. */
  showCount: z.boolean().default(false),
  searchPlaceholder: localized(60),
  // The bar's own look (ADR-0128 amendment 7): defaults keep today's look.
  /** The search box: bordered, filled, round or a line under it. */
  searchStyle: z.enum(['outline', 'filled', 'pill', 'underline']).default('outline'),
  searchIcon: z.boolean().default(false),
  searchWidth: z.enum(['auto', 'full']).default('auto'),
  /** Sorting as a drop-down list or as buttons. */
  sortStyle: z.enum(['select', 'pills']).default('select'),
  /** Category buttons: shape, how the chosen one stands out, and their pictures. */
  chipShape: z.enum(['pill', 'rounded', 'square']).default('pill'),
  chipActive: z.enum(['solid', 'outline', 'soft']).default('solid'),
  chipImages: z.boolean().default(true),
  /** Category buttons in one line that swipes on phones, instead of wrapping. */
  chipScroll: z.boolean().default(false),
  /** Search and sort with the categories in one row, or the categories in their own row. */
  barLayout: z.enum(['row', 'stacked']).default('row'),
  barStyle: z.enum(['plain', 'card', 'soft']).default('plain'),
  barAlign: z.enum(['start', 'center']).default('start'),
  controlSize: z.enum(['small', 'regular', 'large']).default('regular'),
});

/** An offer that ends at a moment, with a live countdown; it hides itself once ended. */
const countdownSectionSchema = z.object({
  ...base,
  type: z.literal('countdown'),
  variant: z.enum(['banner', 'card', 'minimal']).default('banner'),
  title: localized(80),
  subtitle: localized(200),
  endsAt: moment,
  button: localized(30),
  link,
});
/** Words from customers, entered by the merchant (never invented by the platform). */
const testimonialsSectionSchema = z.object({
  ...base,
  type: z.literal('testimonials'),
  variant: z.enum(['cards', 'carousel', 'quote']).default('cards'),
  title: localized(80),
  items: z
    .array(
      z.object({
        id,
        name: text(60),
        detail: localized(60),
        quote: localized(400),
        rating: z.number().int().min(0).max(5).default(5),
      }),
    )
    .max(12)
    .default([]),
});
const faqSectionSchema = z.object({
  ...base,
  type: z.literal('faq'),
  variant: z.enum(['accordion', 'columns']).default('accordion'),
  title: localized(80),
  items: z
    .array(z.object({ id, question: localized(160), answer: localized(1000) }))
    .max(15)
    .default([]),
});
const gallerySectionSchema = z.object({
  ...base,
  type: z.literal('gallery'),
  variant: z.enum(['grid', 'carousel', 'mosaic', 'masonry']).default('grid'),
  title: localized(80),
  columns: z.number().int().min(2).max(4).default(3),
  items: z
    .array(z.object({ id, imageId: image, caption: localized(80), link }))
    .max(12)
    .default([]),
});
const logosSectionSchema = z.object({
  ...base,
  type: z.literal('logos'),
  variant: z.enum(['row', 'grid']).default('row'),
  title: localized(80),
  grayscale: z.boolean().default(true),
  items: z
    .array(z.object({ id, imageId: image, alt: localized(60), link }))
    .max(16)
    .default([]),
});
/** Two to four promotion tiles with pictures; `mosaic` makes the first one big. */
const collageSectionSchema = z.object({
  ...base,
  type: z.literal('collage'),
  variant: z.enum(['mosaic', 'even']).default('mosaic'),
  tiles: z
    .array(
      z.object({
        id,
        imageId: image,
        title: localized(60),
        subtitle: localized(120),
        button: localized(30),
        link,
      }),
    )
    .max(4)
    .default([]),
});
/** A call to action with up to two buttons. */
const ctaSectionSchema = z.object({
  ...base,
  type: z.literal('cta'),
  variant: z.enum(['brand', 'soft', 'outline', 'image']).default('brand'),
  imageId: image,
  title: localized(80),
  text: localized(240),
  button: localized(30),
  link,
  button2: localized(30),
  link2: link,
  align: z.enum(['start', 'center']).default('center'),
  // ADR-0128 amendment 7.
  size: z.enum(['compact', 'regular', 'large']).default('regular'),
  /** `inline` = the words on one side, the buttons on the other. */
  layout: z.enum(['stacked', 'inline']).default('stacked'),
  eyebrow: localized(60),
  buttonStyle: z.enum(['solid', 'outline', 'pill', 'glass']).default('solid'),
  /** The main button gently pulses. */
  pulse: z.boolean().default(false),
  rounded: z.enum(['none', 'soft', 'round']).default('soft'),
});
/** Breathing room between sections, with an optional line. */
const spacerSectionSchema = z.object({
  ...base,
  type: z.literal('spacer'),
  size: z.enum(['small', 'medium', 'large', 'huge']).default('medium'),
  line: z.boolean().default(false),
});

/**
 * Section types that draw their own title with the frame's heading; the others get the
 * frame's `heading` above them (ADR-0128 amendment 7).
 */
export const OWN_TITLE_SECTIONS: readonly SectionType[] = [
  'products',
  'categories',
  'features',
  'menu',
  'video',
  'faq',
  'gallery',
  'logos',
  'testimonials',
  'cards',
  'columns',
  'stats',
  'steps',
  'tabs',
];

// -------------------------------------------------- layout and motion (ADR-0128 amendment 7)
/** A visual separator: a line in several styles, optionally with words or an emoji in it. */
const dividerSectionSchema = z.object({
  ...base,
  type: z.literal('divider'),
  variant: z
    .enum(['line', 'dashed', 'dotted', 'double', 'gradient', 'dots', 'wave', 'zigzag'])
    .default('line'),
  thickness: z.enum(['thin', 'regular', 'thick']).default('thin'),
  length: z.enum(['short', 'half', 'full']).default('full'),
  align: z.enum(['start', 'center', 'end']).default('center'),
  color: z.enum(['border', 'brand', 'fg']).default('border'),
  label: localized(60),
  icon: z.string().trim().max(8).default(''),
});

export const COLUMN_LAYOUTS = ['1', '1-1', '2-1', '1-2', '1-1-1', '1-2-1', '1-1-1-1'] as const;
export type ColumnLayout = (typeof COLUMN_LAYOUTS)[number];
/** How many columns a layout has. */
export const columnCount = (layout: ColumnLayout) => layout.split('-').length;
export const COLUMN_BLOCKS = [
  'heading',
  'text',
  'image',
  'button',
  'list',
  'icon',
  'divider',
  'spacer',
] as const;
export type ColumnBlockType = (typeof COLUMN_BLOCKS)[number];
/** One piece of a column; each kind uses the fields it needs. */
const columnBlockSchema = z.object({
  id,
  type: z.enum(COLUMN_BLOCKS),
  /** Heading, paragraph, button label, or list lines (one per line). */
  text: localized(1000),
  size: z.enum(['small', 'regular', 'large']).default('regular'),
  align: z.enum(['start', 'center', 'end']).default('start'),
  imageId: image,
  link,
  /** Button: solid, outline, soft or a plain link; image: rounded or round. */
  style: z.enum(['solid', 'outline', 'soft', 'link']).default('solid'),
  icon: z.string().trim().max(8).default(''),
});
export type ColumnBlock = z.infer<typeof columnBlockSchema>;
/**
 * Columns and rows of free content (headings, words, pictures, buttons, lists, icons, lines):
 * grid and flexible layouts, split sections, vertical dividers, and on phones stacked rows,
 * reversed rows or a sideways scroll.
 */
const columnsSectionSchema = z.object({
  ...base,
  type: z.literal('columns'),
  title: localized(80),
  layout: z.enum(COLUMN_LAYOUTS).default('1-1'),
  gap: z.enum(['none', 'small', 'regular', 'large']).default('regular'),
  verticalAlign: z.enum(['top', 'center', 'bottom', 'stretch']).default('top'),
  columnStyle: z.enum(['plain', 'card', 'soft', 'outline', 'glass']).default('plain'),
  /** A line between the columns. */
  dividers: z.boolean().default(false),
  phone: z.enum(['stack', 'reverse', 'scroll']).default('stack'),
  columns: z
    .array(z.object({ id, blocks: z.array(columnBlockSchema).max(10).default([]) }))
    .min(1)
    .max(4)
    .default([
      { id: 'col-1', blocks: [] },
      { id: 'col-2', blocks: [] },
    ]),
});

/** A picture beside words: the classic split (two-column) section. */
const splitSectionSchema = z.object({
  ...base,
  type: z.literal('split'),
  imageId: image,
  imagePosition: z.enum(['start', 'end']).default('start'),
  /** `image` = a wider picture, `text` = wider words. */
  ratio: z.enum(['even', 'image', 'text']).default('even'),
  imageShape: z.enum(['rect', 'rounded', 'circle', 'arch', 'blob']).default('rounded'),
  verticalAlign: z.enum(['top', 'center']).default('center'),
  eyebrow: localized(60),
  title: localized(120),
  body: localized(1500),
  button: localized(30),
  link,
  button2: localized(30),
  link2: link,
});

/** A grid of cards, each with a picture or an emoji, words and a link. */
const cardsSectionSchema = z.object({
  ...base,
  type: z.literal('cards'),
  variant: z.enum(['bordered', 'shadow', 'plain', 'overlay', 'glass']).default('bordered'),
  title: localized(80),
  columns: z.number().int().min(2).max(4).default(3),
  mobileColumns: z.union([z.literal(1), z.literal(2)]).default(1),
  aspect: z.enum(['landscape', 'square', 'portrait']).default('landscape'),
  align: z.enum(['start', 'center']).default('start'),
  hover: z.enum(['none', 'lift', 'zoom', 'glow']).default('lift'),
  items: z
    .array(
      z.object({
        id,
        imageId: image,
        icon: z.string().trim().max(8).default(''),
        title: localized(80),
        text: localized(300),
        button: localized(30),
        link,
      }),
    )
    .max(12)
    .default([]),
});

/** Content behind tabs: each tab with a title, words, a picture and a button. */
const tabsSectionSchema = z.object({
  ...base,
  type: z.literal('tabs'),
  variant: z.enum(['pills', 'underline', 'boxed', 'vertical']).default('pills'),
  title: localized(80),
  align: z.enum(['start', 'center']).default('start'),
  imagePosition: z.enum(['start', 'end', 'top']).default('start'),
  tabs: z
    .array(
      z.object({
        id,
        label: localized(30),
        title: localized(80),
        text: localized(1000),
        imageId: image,
        button: localized(30),
        link,
      }),
    )
    .max(8)
    .default([]),
});

/** Words that keep sliding across the screen (offers, values, news). */
const marqueeSectionSchema = z.object({
  ...base,
  type: z.literal('marquee'),
  variant: z.enum(['plain', 'brand', 'dark', 'outline', 'tilted']).default('brand'),
  speed: z.enum(['slow', 'normal', 'fast']).default('normal'),
  direction: z.enum(['start', 'end']).default('start'),
  size: z.enum(['small', 'regular', 'large']).default('regular'),
  separator: z.string().trim().max(8).default('✦'),
  pauseOnHover: z.boolean().default(true),
  items: z
    .array(z.object({ id, text: localized(80) }))
    .max(10)
    .default([]),
});

/** Numbers that count up when they come into view. */
const statsSectionSchema = z.object({
  ...base,
  type: z.literal('stats'),
  variant: z.enum(['row', 'cards', 'boxed']).default('row'),
  title: localized(80),
  countUp: z.boolean().default(true),
  items: z
    .array(
      z.object({
        id,
        value: z.number().int().min(0).max(999_999_999).default(0),
        prefix: text(6),
        suffix: text(6),
        label: localized(60),
      }),
    )
    .max(6)
    .default([]),
});

/** How it works: numbered steps, a timeline or cards. */
const stepsSectionSchema = z.object({
  ...base,
  type: z.literal('steps'),
  variant: z.enum(['numbers', 'timeline', 'cards']).default('numbers'),
  title: localized(80),
  items: z
    .array(
      z.object({
        id,
        icon: z.string().trim().max(8).default(''),
        title: localized(60),
        text: localized(240),
      }),
    )
    .max(8)
    .default([]),
});

export const sectionSchema = z.discriminatedUnion('type', [
  sliderSectionSchema,
  bannerSectionSchema,
  productsSectionSchema,
  categoriesSectionSchema,
  imageSectionSchema,
  videoSectionSchema,
  textSectionSchema,
  featuresSectionSchema,
  menuSectionSchema,
  infoSectionSchema,
  catalogSectionSchema,
  countdownSectionSchema,
  testimonialsSectionSchema,
  faqSectionSchema,
  gallerySectionSchema,
  logosSectionSchema,
  collageSectionSchema,
  ctaSectionSchema,
  spacerSectionSchema,
  dividerSectionSchema,
  columnsSectionSchema,
  splitSectionSchema,
  cardsSectionSchema,
  tabsSectionSchema,
  marqueeSectionSchema,
  statsSectionSchema,
  stepsSectionSchema,
]);
export type DesignSection = z.infer<typeof sectionSchema>;
export type SectionOf<T extends SectionType> = Extract<DesignSection, { type: T }>;

// --------------------------------------------------------------------------- the shell
const navChildSchema = z.object({ id, label: localized(30), link: designLinkSchema });
/**
 * A navigation item: a link, optionally with a sub-menu, or `categories` (a menu of the store's
 * categories, shown as a list or a picture mega-menu by `navbar.menuStyle`).
 */
const navItemSchema = z.object({
  id,
  label: localized(30),
  link: designLinkSchema,
  kind: z.enum(['link', 'categories']).default('link'),
  children: z.array(navChildSchema).max(10).default([]),
});
export type NavItem = z.infer<typeof navItemSchema>;

export const HEADER_LAYOUTS = [
  'classic',
  'centered',
  'split',
  'stacked',
  'minimal',
  'floating',
  'bold',
] as const;
export type HeaderLayout = (typeof HEADER_LAYOUTS)[number];
const HEADER_ACTIONS = ['search', 'account', 'branch', 'language', 'whatsapp', 'cart'] as const;
export type HeaderAction = (typeof HEADER_ACTIONS)[number];
const DEFAULT_ACTIONS: { type: HeaderAction; visible: boolean }[] = [
  { type: 'search', visible: true },
  { type: 'account', visible: true },
  { type: 'branch', visible: true },
  { type: 'language', visible: true },
  { type: 'whatsapp', visible: false },
  { type: 'cart', visible: true },
];

/** Header items in the merchant's order: each kind once, the cart always shown. */
const actionsSchema = z
  .array(z.object({ type: z.enum(HEADER_ACTIONS), visible: z.boolean() }))
  .max(HEADER_ACTIONS.length * 2)
  .default(DEFAULT_ACTIONS)
  .transform((list) => {
    const seen = new Set<HeaderAction>();
    const out = list.filter((a) => (seen.has(a.type) ? false : (seen.add(a.type), true)));
    for (const a of DEFAULT_ACTIONS)
      if (!seen.has(a.type)) out.push({ ...a, visible: a.type === 'cart' });
    return out.map((a) => (a.type === 'cart' ? { ...a, visible: true } : a));
  });

const headerSchema = z.preprocess(
  (value) => {
    if (!value || typeof value !== 'object') return value;
    const h = value as Record<string, unknown>;
    // Designs saved before the header system v2 (ADR-0128 amendment 1) had `showSearch`.
    if (h['actions'] === undefined && h['showSearch'] === false)
      return {
        ...h,
        actions: DEFAULT_ACTIONS.map((a) => (a.type === 'search' ? { ...a, visible: false } : a)),
      };
    return h;
  },
  z.object({
    layout: z.enum(HEADER_LAYOUTS).default('classic'),
    background: z.enum(['light', 'brand', 'dark', 'glass']).default('light'),
    border: z.enum(['none', 'line', 'shadow']).default('line'),
    height: z.enum(['compact', 'regular', 'tall']).default('regular'),
    width: z.enum(['contained', 'full']).default('contained'),
    sticky: z.boolean().default(true),
    /** Slides away while scrolling down, comes back when scrolling up. */
    hideOnScroll: z.boolean().default(false),
    logoSize: z.enum(['small', 'medium', 'large']).default('medium'),
    /** `wide` = a word-mark shown whole, without the round crop (ADR-0146). */
    logoShape: z.enum(['round', 'wide']).default('round'),
    showName: z.boolean().default(true),
    showTagline: z.boolean().default(true),
    /** Search as an icon, or as a search bar in the header (the search-first layout always shows the bar). */
    searchStyle: z.enum(['icon', 'bar']).default('icon'),
    cartStyle: z.enum(['pill', 'icon', 'text']).default('pill'),
    languageStyle: z.enum(['compact', 'full']).default('compact'),
    actions: actionsSchema,
    mobileLayout: z.enum(['start', 'center']).default('start'),
    /** A search bar under the header on phones. */
    mobileSearch: z.boolean().default(false),
  }),
);
// ---------------------------------------------------------------------------- footer
/** Footer arrangements (ADR-0128 amendment 2); each one also works on phones. */
export const FOOTER_LAYOUTS = [
  'columns',
  'split',
  'stacked',
  'centered',
  'cards',
  'simple',
  'minimal',
] as const;
export type FooterLayout = (typeof FOOTER_LAYOUTS)[number];
export const FOOTER_BLOCK_TYPES = [
  'about',
  'links',
  'categories',
  'contact',
  'hours',
  'branches',
  'social',
  'payments',
  'text',
] as const;
export type FooterBlockType = (typeof FOOTER_BLOCK_TYPES)[number];

const footerLinkSchema = z.object({ id, label: localized(30), link: designLinkSchema });
export type FooterLink = z.infer<typeof footerLinkSchema>;

const footerBlockSchema = z.object({
  id,
  type: z.enum(FOOTER_BLOCK_TYPES),
  title: localized(40),
  text: localized(400),
  links: z.array(footerLinkSchema).max(10).default([]),
});
export type FooterBlock = z.infer<typeof footerBlockSchema>;

const footerBadgeSchema = z.object({
  id,
  icon: z.string().trim().max(8).default(''),
  title: localized(40),
  text: localized(80),
});
export type FooterBadge = z.infer<typeof footerBadgeSchema>;

/**
 * The first designs had one `style` (simple / columns / centred / dark); it maps onto the layout
 * and the colour, and those footers keep their bottom line as it was (no back-to-top button,
 * no copyright in the simple one).
 */
function footerCompat(input: unknown): unknown {
  if (!input || typeof input !== 'object') return input;
  const old = input as Record<string, unknown> & { style?: unknown };
  if (old.layout !== undefined || typeof old.style !== 'string') return input;
  const { style, ...rest } = old;
  const simple = style === 'simple';
  return {
    layout: style === 'dark' ? 'columns' : style,
    background: style === 'dark' ? 'dark' : 'light',
    topEdge: simple ? 'none' : 'line',
    mobileCollapse: false,
    bottom: { showCopyright: !simple, showBackToTop: false },
    ...rest,
  };
}

const footerSchema = z.preprocess(
  footerCompat,
  z.object({
    layout: z.enum(FOOTER_LAYOUTS).default('columns'),
    background: z.enum(['light', 'tinted', 'brand', 'dark']).default('light'),
    /** The edge between the page and the footer. */
    topEdge: z.enum(['none', 'line', 'wave', 'curve']).default('line'),
    width: z.enum(['contained', 'full']).default('contained'),
    spacing: z.enum(['compact', 'regular', 'roomy']).default('regular'),
    /** Columns of blocks on large screens; auto = one per block, at most four. */
    columns: z.enum(['auto', '2', '3', '4']).default('auto'),
    /** The store's logo, name and words (split, stacked, centred and cards layouts). */
    brand: z
      .object({
        showLogo: z.boolean().default(true),
        showName: z.boolean().default(true),
        showTagline: z.boolean().default(true),
        /** Replaces the tagline when set. */
        text: localized(300),
      })
      .prefault({}),
    /** A call-to-action band at the top of the footer. */
    cta: z
      .object({
        enabled: z.boolean().default(false),
        title: localized(80),
        text: localized(200),
        button: localized(30),
        link: designLinkSchema.nullable().default(null),
      })
      .prefault({}),
    /** Reassurance strip: emoji + short words (fast delivery, secure payment…). */
    badges: z.array(footerBadgeSchema).max(4).default([]),
    socialStyle: z.enum(['icons', 'filled', 'labels']).default('icons'),
    blocks: z.array(footerBlockSchema).max(6).default([]),
    bottom: z
      .object({
        showCopyright: z.boolean().default(true),
        /** Replaces "© year store name" when set. */
        copyright: localized(120),
        /** Policies and other small links (returns, privacy, terms…). */
        links: z.array(footerLinkSchema).max(6).default([]),
        showPayments: z.boolean().default(false),
        showLanguage: z.boolean().default(false),
        showBackToTop: z.boolean().default(true),
      })
      .prefault({}),
    showPoweredBy: z.boolean().default(true),
    /** On phones the titled blocks fold into tappable rows. */
    mobileCollapse: z.boolean().default(true),
  }),
);
export type FooterDesign = z.output<typeof footerSchema>;

// ---------------------------------------------------------------------- product page
/**
 * How the page of one product is drawn (ADR-0128 amendment 4). Each arrangement also works
 * on phones, where the pictures always come first.
 */
export const PRODUCT_LAYOUTS = [
  'classic',
  'wide',
  'reverse',
  'stacked',
  'scroll',
  'compact',
] as const;
export type ProductLayout = (typeof PRODUCT_LAYOUTS)[number];

const productHighlightSchema = z.object({
  id,
  icon: z.string().trim().max(8).default(''),
  title: localized(40),
  text: localized(80),
});
export type ProductHighlight = z.infer<typeof productHighlightSchema>;

/** Store-wide words shown with every product's description (delivery, returns, care…). */
const productInfoBlockSchema = z.object({ id, title: localized(40), text: localized(1000) });
export type ProductInfoBlock = z.infer<typeof productInfoBlockSchema>;

const productPageSchema = z.object({
  layout: z.enum(PRODUCT_LAYOUTS).default('classic'),
  gallery: z
    .object({
      /** Where the small pictures go; `side` falls back to `below` on phones. */
      thumbs: z.enum(['below', 'side', 'dots', 'none']).default('below'),
      ratio: z.enum(['square', 'portrait', 'landscape']).default('square'),
      fit: z.enum(['cover', 'contain']).default('cover'),
      /** Tap a picture to see it full screen. */
      zoom: z.boolean().default(true),
    })
    .prefault({}),
  info: z
    .object({
      /** Home › category › product, above the name. */
      breadcrumb: z.boolean().default(true),
      /** The product's category as a chip above the name. */
      category: z.boolean().default(false),
      sku: z.boolean().default(false),
      /** SKU, categories and brand in a line under the details (ADR-0149). */
      meta: z.boolean().default(false),
      share: z.boolean().default(true),
      /** "In stock" / "Sold out" beside the price. */
      stock: z.boolean().default(true),
      /** "Only N left" when fewer can be ordered; 0 = never. */
      lowStockAt: z.number().int().min(0).max(10).default(0),
      titleSize: z.enum(['regular', 'large']).default('regular'),
      priceSize: z.enum(['regular', 'large']).default('large'),
      align: z.enum(['start', 'center']).default('start'),
    })
    .prefault({}),
  buy: z
    .object({
      /** How a product's options are chosen. */
      options: z.enum(['buttons', 'dropdown']).default('buttons'),
      quantity: z.enum(['stepper', 'dropdown', 'hidden']).default('stepper'),
      /** Replaces "Add to cart" when set. */
      addLabel: localized(30),
      /** A second button that adds the product and opens the cart. */
      buyNow: z.boolean().default(false),
      /** On phones the add button stays at the bottom of the screen. */
      stickyBar: z.boolean().default(true),
      /** "Ask on WhatsApp" with the product's name and link (needs the store's WhatsApp number). */
      whatsapp: z.boolean().default(false),
      /** "Leave a note" box: the shopper's note travels with the item (ADR-0160). */
      note: z.boolean().default(false),
    })
    .prefault({}),
  /** Reassurance under the buttons: emoji + short words. */
  highlights: z.array(productHighlightSchema).max(4).default([]),
  description: z
    .object({
      style: z.enum(['plain', 'tabs', 'accordion']).default('plain'),
      blocks: z.array(productInfoBlockSchema).max(4).default([]),
    })
    .prefault({}),
  related: z
    .object({
      enabled: z.boolean().default(true),
      title: localized(80),
      /** Same category first; the featured, then any, fill the rest. */
      source: z.enum(['category', 'featured', 'all']).default('category'),
      limit: z.number().int().min(2).max(12).default(4),
      variant: z.enum(['grid', 'carousel']).default('carousel'),
      showAdd: z.boolean().default(false),
    })
    .prefault({}),
  /** The products this shopper opened before, kept on the shopper's device only. */
  recent: z.object({ enabled: z.boolean().default(false), title: localized(80) }).prefault({}),
});
export type ProductPageDesign = z.output<typeof productPageSchema>;

// ------------------------------------------------------------- cart and checkout pages
/**
 * The cart and checkout pages (ADR-0128 amendment 5). Only how they look and what they ask is
 * designed: prices, fees, promotions and stock always come from the server.
 */
const cartPageSchema = z.object({
  /** `split` = the summary beside the lines on large screens; `stacked` = under them. */
  layout: z.enum(['split', 'stacked']).default('split'),
  /** Product pictures beside the lines. */
  images: z.boolean().default(true),
  /** A short note above the lines (delivery times, a gift note…). */
  message: localized(200),
  /** How far the cart is from the store's minimum order. */
  minimumBar: z.boolean().default(true),
  /** Replaces "Checkout" when set. */
  checkoutLabel: localized(30),
  /** On phones the total and the checkout button stay at the bottom of the screen. */
  stickyCheckout: z.boolean().default(true),
  continueShopping: z.boolean().default(true),
  /** Reassurance under the checkout button. */
  highlights: z.array(productHighlightSchema).max(4).default([]),
  /** Products offered with the cart (never one already in it, never a sold-out one). */
  upsell: z
    .object({
      enabled: z.boolean().default(true),
      title: localized(80),
      source: z.enum(['featured', 'all']).default('featured'),
      limit: z.number().int().min(2).max(8).default(4),
      variant: z.enum(['grid', 'carousel']).default('carousel'),
    })
    .prefault({}),
  /** The empty cart: own words and products to start from. */
  empty: z
    .object({
      title: localized(80),
      text: localized(200),
      showProducts: z.boolean().default(true),
    })
    .prefault({}),
});
export type CartPageDesign = z.output<typeof cartPageSchema>;

const checkoutPageSchema = z.object({
  /** `split` = the order summary beside the form on large screens; `stacked` = one column. */
  layout: z.enum(['split', 'stacked']).default('split'),
  /** Numbered steps: details, receiving, payment. */
  steps: z.boolean().default(true),
  /** Pickup / delivery and the payment methods as large cards or a plain list. */
  choices: z.enum(['cards', 'list']).default('cards'),
  message: localized(200),
  email: z.enum(['optional', 'required', 'hidden']).default('optional'),
  note: z
    .object({
      enabled: z.boolean().default(true),
      /** Replaces "Note" when set ("Notes for the kitchen"…). */
      label: localized(60),
    })
    .prefault({}),
  /** The promotion code box; automatic offers apply either way. */
  promo: z.boolean().default(true),
  /** Product pictures in the order summary. */
  images: z.boolean().default(true),
  /** "I agree to the terms" before ordering, with a link to them. */
  terms: z
    .object({
      enabled: z.boolean().default(false),
      text: localized(200),
      link: designLinkSchema.nullable().default(null),
    })
    .prefault({}),
  /** Replaces "Place order and pay" when set. */
  submitLabel: localized(30),
  highlights: z.array(productHighlightSchema).max(4).default([]),
});
export type CheckoutPageDesign = z.output<typeof checkoutPageSchema>;

// ------------------------------------------------------------- discounts (ADR-0133)
/** What the badge on a discounted product says: "-20%", "Save 0.250", "Sale" or own words. */
export const SALE_BADGE_TEXTS = ['percent', 'amount', 'word', 'custom'] as const;
export const SALE_BADGE_COLOURS = [
  'red',
  'brand',
  'dark',
  'green',
  'orange',
  'pink',
  'gold',
  'custom',
] as const;
export const SALE_BADGE_SIZES = ['sm', 'md', 'lg', 'xl'] as const;
export const SALE_BADGE_SHAPES = ['pill', 'rounded', 'square', 'circle', 'ribbon', 'tag'] as const;
export const SALE_BADGE_POSITIONS = ['top-start', 'top-end', 'bottom-start', 'bottom-end'] as const;
export const SALE_BADGE_STYLES = ['solid', 'soft', 'outline'] as const;

/** The discount badge on a product's picture (cards in lists, or the product page). */
const saleBadgeSchema = z.object({
  show: z.boolean().default(true),
  text: z.enum(SALE_BADGE_TEXTS).default('percent'),
  /** Own words for `custom` (e.g. "Eid offer"). */
  customText: localized(20),
  colour: z.enum(SALE_BADGE_COLOURS).default('red'),
  customColour: hexColor,
  style: z.enum(SALE_BADGE_STYLES).default('solid'),
  size: z.enum(SALE_BADGE_SIZES).default('md'),
  shape: z.enum(SALE_BADGE_SHAPES).default('pill'),
  position: z.enum(SALE_BADGE_POSITIONS).default('top-start'),
  /** A gentle pulse that draws the eye (still under reduced motion). */
  pulse: z.boolean().default(false),
});
export type SaleBadgeDesign = z.infer<typeof saleBadgeSchema>;

/**
 * How discounted products show (ADR-0133): the badge on cards and on the product page, the old
 * price struck through, the colour of the new price, the saving and the end of the offer.
 * Prices themselves always come from the server; this only decides how they look.
 */
const saleDesignSchema = z.object({
  card: saleBadgeSchema.prefault({}),
  page: saleBadgeSchema.prefault({ size: 'lg' }),
  /** The price before the discount, struck through beside the new one. */
  oldPrice: z.boolean().default(true),
  /** The new price in the badge's colour, the store colour, or like any price. */
  priceColour: z.enum(['badge', 'brand', 'default']).default('badge'),
  /** "You save 0.250 KWD" on the product page. */
  savings: z.boolean().default(true),
  /** "Ends in 2 days" on the product page when the discount has an end. */
  endsIn: z.boolean().default(true),
});

const handle = z
  .string()
  .trim()
  .max(60)
  .regex(/^[A-Za-z0-9._-]*$/, 'Letters, digits, dots, dashes and underscores only.')
  .default('');

export const storeDesignSchema = z
  .object({
    template: z.enum(DESIGN_TEMPLATES).default('retail'),
    style: z
      .object({
        radius: z.enum(['sharp', 'soft', 'round']).default('soft'),
        background: z.enum(['white', 'tinted', 'dark']).default('white'),
        cards: z.enum(['bordered', 'shadow', 'flat']).default('bordered'),
        buttons: z.enum(['solid', 'pill']).default('solid'),
        /**
         * Motion of the whole page (ADR-0128 amendment 7): sections rise into view (`subtle`),
         * and list items follow one after another with cards lifting on hover (`lively`).
         */
        motion: z.enum(['none', 'subtle', 'lively']).default('subtle'),
        /** The store's typeface (ADR-0146): the link page's self-hosted fonts. */
        font: z.enum(LINK_FONTS).default('default'),
      })
      .prefault({}),
    /** The top bar: the store's announcement and up to three rotating messages, with shortcuts. */
    announcement: z
      .object({
        style: z.enum(['brand', 'dark', 'soft']).default('brand'),
        messages: z.array(localized(120)).max(3).default([]),
        showContact: z.boolean().default(false),
        showSocial: z.boolean().default(false),
      })
      .prefault({}),
    header: headerSchema.prefault({}),
    navbar: z
      .object({
        style: z.enum(['links', 'underline', 'pills', 'boxed', 'hidden']).default('links'),
        align: z.enum(['start', 'center', 'end']).default('center'),
        menuStyle: z.enum(['dropdown', 'mega']).default('mega'),
        items: z.array(navItemSchema).max(8).default([]),
      })
      .prefault({}),
    drawer: z
      .object({
        enabled: z.boolean().default(true),
        style: z.enum(['side', 'full']).default('side'),
        showCategories: z.boolean().default(true),
        showContact: z.boolean().default(true),
        showSocial: z.boolean().default(true),
      })
      .prefault({}),
    footer: footerSchema.prefault({}),
    productPage: productPageSchema.prefault({}),
    /** Discounted products: badge, old price and saving (ADR-0133). */
    sale: saleDesignSchema.prefault({}),
    cartPage: cartPageSchema.prefault({}),
    checkoutPage: checkoutPageSchema.prefault({}),
    social: z
      .object({
        instagram: handle,
        tiktok: handle,
        snapchat: handle,
        x: handle,
        youtube: handle,
        whatsapp: z
          .string()
          .trim()
          .max(20)
          .regex(/^\+?[0-9]*$/, 'Digits only, with the country code.')
          .default(''),
      })
      .default({ instagram: '', tiktok: '', snapchat: '', x: '', youtube: '', whatsapp: '' }),
    mobileBar: z.object({ enabled: z.boolean().default(true) }).default({ enabled: true }),
    pages: z.object({
      home: z.array(sectionSchema).max(30),
      products: z.array(sectionSchema).max(15),
      categories: z.array(sectionSchema).max(15),
      /** Sections under the details of every product (reviews, questions, banners…). */
      product: z.array(sectionSchema).max(15).default([]),
      /** Sections under the cart (reassurance, questions, an offer…). */
      cart: z.array(sectionSchema).max(10).default([]),
    }),
  })
  .superRefine((d, ctx) => {
    // The products page lists the products (catalog or menu); the categories page lists them.
    if (!d.pages.products.some((s) => s.type === 'catalog' || s.type === 'menu'))
      ctx.addIssue({
        code: 'custom',
        path: ['pages', 'products'],
        message: 'The products page needs a product list (catalog or menu).',
      });
    if (!d.pages.categories.some((s) => s.type === 'categories'))
      ctx.addIssue({
        code: 'custom',
        path: ['pages', 'categories'],
        message: 'The categories page needs a categories section.',
      });
    // The product page shows one product: lists with their own search do not belong there.
    for (const page of ['product', 'cart'] as const)
      if (d.pages[page].some((s) => s.type === 'catalog' || s.type === 'menu'))
        ctx.addIssue({
          code: 'custom',
          path: ['pages', page],
          message: `The ${page} page cannot hold a catalog or a menu.`,
        });
    for (const page of DESIGN_PAGES) {
      const ids = d.pages[page].map((s) => s.id);
      if (new Set(ids).size !== ids.length)
        ctx.addIssue({
          code: 'custom',
          path: ['pages', page],
          message: 'Two sections share an id.',
        });
    }
  });
export type StoreDesign = z.infer<typeof storeDesignSchema>;
export type StoreDesignInput = z.input<typeof storeDesignSchema>;

/** Every image a design refers to (to refuse deleting one in use). */
export function designImageIds(design: StoreDesign): string[] {
  const ids: (string | null)[] = [];
  for (const page of DESIGN_PAGES)
    for (const s of design.pages[page]) {
      if (s.type === 'slider') ids.push(...s.slides.flatMap((x) => [x.imageId, x.mobileImageId]));
      if (s.type === 'banner' || s.type === 'image' || s.type === 'cta') ids.push(s.imageId);
      if (s.type === 'gallery' || s.type === 'logos') ids.push(...s.items.map((x) => x.imageId));
      if (s.type === 'collage') ids.push(...s.tiles.map((x) => x.imageId));
      if (s.type === 'banner') ids.push(s.mobileImageId);
      if (s.type === 'split') ids.push(s.imageId);
      if (s.type === 'cards') ids.push(...s.items.map((x) => x.imageId));
      if (s.type === 'tabs') ids.push(...s.tabs.map((x) => x.imageId));
      if (s.type === 'columns')
        for (const c of s.columns) ids.push(...c.blocks.map((b) => b.imageId));
      ids.push(s.look.bgImageId);
    }
  return [...new Set(ids.filter((x): x is string => Boolean(x)))];
}

// ----------------------------------------------------------------------- templates
const l = (en: string, ar: string): Localized => ({ en, ar });
const parse = (d: StoreDesignInput) => storeDesignSchema.parse(d);

/**
 * The layout of a store that never published a design: today's storefront (the store banner,
 * then the catalog with search, sort, categories and featured products).
 */
export function legacyDesign(template: DesignTemplate = 'retail'): StoreDesign {
  return parse({
    template,
    footer: {
      layout: 'simple',
      topEdge: 'none',
      mobileCollapse: false,
      blocks: [{ id: 'contact', type: 'contact' }],
      bottom: { showCopyright: false, showBackToTop: false },
      showPoweredBy: false,
    },
    drawer: { enabled: false },
    mobileBar: { enabled: false },
    navbar: { style: 'links', items: [] },
    header: { layout: 'classic', showSearch: false },
    pages: {
      home: [
        { id: 'banner', type: 'banner', variant: 'overlay', imageSource: 'store' },
        { id: 'catalog', type: 'catalog' },
      ],
      products: [{ id: 'catalog', type: 'catalog' }],
      categories: [{ id: 'categories', type: 'categories', variant: 'tiles' }],
    },
  });
}

/** One-click footers offered in the console (ADR-0128 amendment 2). */
export const FOOTER_PRESETS = ['shop', 'restaurant', 'brand', 'minimal'] as const;
export type FooterPreset = (typeof FOOTER_PRESETS)[number];

/** Reassurance badges suggested to every store (the merchant edits or removes them). */
export function suggestedBadges(template: DesignTemplate = 'retail'): FooterBadge[] {
  const badge = (id: string, icon: string, title: Localized, text: Localized): FooterBadge => ({
    id,
    icon,
    title,
    text,
  });
  return template === 'restaurant'
    ? [
        badge(
          'badge-fresh',
          '🍳',
          l('Made fresh', 'يُحضّر طازجًا'),
          l('Cooked when you order', 'نجهّزه عند طلبك'),
        ),
        badge(
          'badge-fast',
          '🛵',
          l('Fast delivery', 'توصيل سريع'),
          l('Hot to your door', 'يصلك ساخنًا'),
        ),
        badge(
          'badge-pay',
          '💳',
          l('Pay your way', 'ادفع بطريقتك'),
          l('Card, link or on receipt', 'بطاقة أو رابط أو عند الاستلام'),
        ),
      ]
    : [
        badge(
          'badge-delivery',
          '🚚',
          l('Fast delivery', 'توصيل سريع'),
          l('To your door', 'لين باب البيت'),
        ),
        badge(
          'badge-secure',
          '🔒',
          l('Secure payment', 'دفع آمن'),
          l('Your data is protected', 'بياناتك محمية'),
        ),
        badge(
          'badge-support',
          '💬',
          l('We are here to help', 'جاهزين نساعدك'),
          l('Message us any time', 'راسلنا في أي وقت'),
        ),
      ];
}

export function footerPreset(preset: FooterPreset): FooterDesign {
  const block = (id: string, type: FooterBlockType, title: Localized) => ({ id, type, title });
  const quickLinks = {
    ...block('links', 'links', l('Quick links', 'روابط سريعة')),
    links: [
      {
        id: 'link-products',
        label: l('All products', 'كل المنتجات'),
        link: { kind: 'page', page: 'products' },
      },
      {
        id: 'link-categories',
        label: l('Categories', 'التصنيفات'),
        link: { kind: 'page', page: 'categories' },
      },
      {
        id: 'link-account',
        label: l('My account', 'حسابي'),
        link: { kind: 'page', page: 'account' },
      },
    ],
  };
  const input = {
    shop: {
      layout: 'split',
      background: 'tinted',
      topEdge: 'line',
      badges: suggestedBadges('retail'),
      blocks: [
        block('categories', 'categories', l('Shop', 'تسوّق')),
        quickLinks,
        block('contact', 'contact', l('Contact us', 'تواصل معنا')),
      ],
      bottom: { showPayments: true },
    },
    restaurant: {
      layout: 'stacked',
      background: 'dark',
      topEdge: 'curve',
      cta: {
        enabled: true,
        title: l('Hungry?', 'جوعان؟'),
        text: l('Order now for pickup or delivery.', 'اطلب الحين استلام أو توصيل.'),
        button: l('Order now', 'اطلب الآن'),
        link: { kind: 'page', page: 'products' },
      },
      blocks: [
        block('hours', 'hours', l('Opening hours', 'أوقات العمل')),
        block('branches', 'branches', l('Our branches', 'فروعنا')),
        block('contact', 'contact', l('Contact us', 'تواصل معنا')),
      ],
      bottom: { showPayments: true },
    },
    brand: {
      layout: 'centered',
      background: 'brand',
      topEdge: 'wave',
      spacing: 'roomy',
      socialStyle: 'filled',
      blocks: [],
      bottom: { links: [] },
    },
    minimal: {
      layout: 'minimal',
      background: 'light',
      topEdge: 'line',
      spacing: 'compact',
      blocks: [],
    },
  }[preset];
  return footerSchema.parse(input);
}

/** One-click product pages offered in the console (ADR-0128 amendment 4). */
export const PRODUCT_PAGE_PRESETS = ['classic', 'fashion', 'food', 'tech', 'minimal'] as const;
export type ProductPagePreset = (typeof PRODUCT_PAGE_PRESETS)[number];

/** Reassurance under the buttons suggested to a product page (the merchant edits them). */
export function suggestedHighlights(kind: 'shop' | 'food' | 'tech' = 'shop'): ProductHighlight[] {
  const h = (id: string, icon: string, title: Localized, text: Localized): ProductHighlight => ({
    id,
    icon,
    title,
    text,
  });
  if (kind === 'food')
    return [
      h(
        'fresh',
        '🔥',
        l('Made fresh', 'يُحضّر طازج'),
        l('Prepared when you order', 'نجهزه وقت طلبك'),
      ),
      h(
        'delivery',
        '🛵',
        l('Pickup or delivery', 'استلام أو توصيل'),
        l('Your choice', 'على راحتك'),
      ),
    ];
  if (kind === 'tech')
    return [
      h(
        'genuine',
        '✅',
        l('Genuine products', 'منتجات أصلية'),
        l('From trusted sources', 'من مصادر موثوقة'),
      ),
      h('warranty', '🛡️', l('Warranty', 'ضمان'), l('Ask us about the terms', 'اسألنا عن الشروط')),
      h('delivery', '🚚', l('Fast delivery', 'توصيل سريع'), l('To your door', 'لين باب بيتك')),
    ];
  return [
    h('delivery', '🚚', l('Fast delivery', 'توصيل سريع'), l('To your door', 'لين باب بيتك')),
    h('secure', '🔒', l('Secure payment', 'دفع آمن'), l('Pay your way', 'ادفع بطريقتك')),
    h(
      'help',
      '💬',
      l('Here to help', 'جاهزين نساعدك'),
      l('Message us any time', 'راسلنا في أي وقت'),
    ),
  ];
}

const deliveryBlock = {
  id: 'delivery',
  title: l('Delivery and returns', 'التوصيل والإرجاع'),
  text: l(
    'Orders are prepared quickly and delivered to your door. Message us if anything is not right.',
    'نجهز الطلب بسرعة ونوصله لين بابك. إذا فيه أي ملاحظة راسلنا ونحلها.',
  ),
};

export function productPagePreset(preset: ProductPagePreset): ProductPageDesign {
  const input = {
    classic: {
      layout: 'classic',
      highlights: suggestedHighlights('shop'),
      related: { title: l('You may also like', 'قد يعجبك أيضًا') },
    },
    fashion: {
      layout: 'scroll',
      gallery: { thumbs: 'none', ratio: 'portrait' },
      info: { category: true, lowStockAt: 3 },
      buy: { options: 'buttons', buyNow: true },
      highlights: suggestedHighlights('shop'),
      description: { style: 'accordion', blocks: [deliveryBlock] },
      related: { title: l('Complete the look', 'كمّل اللوك'), variant: 'grid' },
      recent: { enabled: true, title: l('Recently viewed', 'شاهدتها مؤخرًا') },
    },
    food: {
      layout: 'compact',
      gallery: { thumbs: 'dots', ratio: 'landscape' },
      info: { breadcrumb: false, stock: false, titleSize: 'large' },
      buy: { quantity: 'stepper', addLabel: l('Add to order', 'أضف للطلب') },
      highlights: suggestedHighlights('food'),
      related: { title: l('Goes well with', 'يتناسب معه'), showAdd: true },
    },
    tech: {
      layout: 'wide',
      gallery: { thumbs: 'side', fit: 'contain' },
      info: { sku: true, category: true, lowStockAt: 5 },
      buy: { buyNow: true, whatsapp: true },
      highlights: suggestedHighlights('tech'),
      description: {
        style: 'tabs',
        blocks: [
          deliveryBlock,
          {
            id: 'warranty',
            title: l('Warranty', 'الضمان'),
            text: l(
              'Every product comes with its maker’s warranty. Keep your receipt.',
              'كل منتج عليه ضمان الشركة المصنعة. احتفظ بالفاتورة.',
            ),
          },
        ],
      },
      related: { title: l('Similar products', 'منتجات مشابهة'), variant: 'grid' },
      recent: { enabled: true, title: l('Recently viewed', 'شاهدتها مؤخرًا') },
    },
    minimal: {
      layout: 'stacked',
      gallery: { thumbs: 'dots' },
      info: { breadcrumb: false, share: false, align: 'center' },
      buy: { stickyBar: false },
      related: { title: l('More to explore', 'اكتشف المزيد'), variant: 'grid' },
    },
  }[preset];
  return productPageSchema.parse(input);
}

/** One-click cart and checkout pages, set together (ADR-0128 amendment 5). */
export const CART_PRESETS = ['classic', 'express', 'food', 'minimal'] as const;
export type CartPreset = (typeof CART_PRESETS)[number];

export function cartPreset(preset: CartPreset): {
  cartPage: CartPageDesign;
  checkoutPage: CheckoutPageDesign;
} {
  const secure = [
    {
      id: 'secure',
      icon: '🔒',
      title: l('Secure checkout', 'دفع آمن'),
      text: l('Your details stay private', 'بياناتك محمية'),
    },
    {
      id: 'help',
      icon: '💬',
      title: l('Here to help', 'جاهزين نساعدك'),
      text: l('Message us any time', 'راسلنا في أي وقت'),
    },
  ];
  const input = {
    classic: {
      cartPage: {
        highlights: suggestedHighlights('shop'),
        upsell: { title: l('You may also like', 'قد يعجبك أيضًا') },
      },
      checkoutPage: { highlights: secure },
    },
    express: {
      cartPage: {
        layout: 'stacked',
        upsell: { enabled: false },
        checkoutLabel: l('Checkout now', 'ادفع الآن'),
      },
      checkoutPage: { steps: false, email: 'hidden', note: { enabled: false } },
    },
    food: {
      cartPage: {
        highlights: suggestedHighlights('food'),
        upsell: { title: l('Complete your order', 'كمّل طلبك'), source: 'all' },
        checkoutLabel: l('Continue', 'متابعة'),
      },
      checkoutPage: {
        email: 'hidden',
        note: { label: l('Notes for the kitchen', 'ملاحظات للمطبخ') },
        submitLabel: l('Send my order', 'أرسل طلبي'),
      },
    },
    minimal: {
      cartPage: {
        images: false,
        minimumBar: false,
        upsell: { enabled: false },
        empty: { showProducts: false },
      },
      checkoutPage: { steps: false, choices: 'list', images: false },
    },
  }[preset];
  return {
    cartPage: cartPageSchema.parse(input.cartPage),
    checkoutPage: checkoutPageSchema.parse(input.checkoutPage),
  };
}

/** Starting points offered in the console (ADR-0128): shops and restaurants differ. */
export function templateDesign(template: DesignTemplate): StoreDesign {
  const nav = [
    { id: 'nav-home', label: l('Home', 'الرئيسية'), link: { kind: 'page', page: 'home' } },
    {
      id: 'nav-products',
      label: template === 'restaurant' ? l('Menu', 'المنيو') : l('Shop', 'المنتجات'),
      link: { kind: 'page', page: 'products' },
    },
    {
      id: 'nav-categories',
      label: l('Categories', 'التصنيفات'),
      link: { kind: 'page', page: 'categories' },
      kind: 'categories',
    },
  ] as const;
  if (template === 'restaurant')
    return parse({
      template,
      style: { radius: 'round', background: 'tinted', cards: 'shadow', buttons: 'pill' },
      header: {
        layout: 'centered',
        background: 'light',
        border: 'shadow',
        logoSize: 'large',
        cartStyle: 'pill',
        mobileLayout: 'center',
        actions: [
          { type: 'whatsapp', visible: true },
          { type: 'account', visible: true },
          { type: 'language', visible: true },
          { type: 'cart', visible: true },
        ],
      },
      navbar: { style: 'pills', align: 'center', items: [...nav] },
      footer: footerPreset('restaurant'),
      productPage: productPagePreset('food'),
      ...cartPreset('food'),
      pages: {
        home: [
          {
            id: 'hero',
            type: 'banner',
            variant: 'overlay',
            imageSource: 'store',
            align: 'center',
            title: l('Fresh from our kitchen', 'طازج من مطبخنا'),
            subtitle: l('Order for pickup or delivery', 'اطلب استلام أو توصيل'),
            button: l('Order now', 'اطلب الآن'),
            link: { kind: 'page', page: 'products' },
          },
          { id: 'info', type: 'info', variant: 'strip' },
          {
            id: 'popular',
            type: 'products',
            variant: 'carousel',
            source: 'featured',
            title: l('Most ordered', 'الأكثر طلبًا'),
          },
          { id: 'menu', type: 'menu', variant: 'list', title: l('Menu', 'المنيو') },
        ],
        products: [
          { id: 'info', type: 'info', variant: 'strip' },
          { id: 'menu', type: 'menu', variant: 'list' },
        ],
        categories: [{ id: 'categories', type: 'categories', variant: 'tiles' }],
      },
    });
  return parse({
    template,
    style: { radius: 'soft', background: 'white', cards: 'bordered', buttons: 'solid' },
    header: {
      layout: 'stacked',
      background: 'light',
      border: 'line',
      searchStyle: 'bar',
      cartStyle: 'pill',
      mobileSearch: true,
    },
    navbar: { style: 'underline', align: 'start', menuStyle: 'mega', items: [...nav] },
    footer: footerPreset('shop'),
    productPage: productPagePreset('classic'),
    ...cartPreset('classic'),
    pages: {
      home: [
        {
          id: 'slider',
          type: 'slider',
          variant: 'contained',
          slides: [
            {
              id: 'slide-1',
              title: l('New arrivals', 'وصل حديثًا'),
              subtitle: l('Discover what is new this week', 'اكتشف جديد هذا الأسبوع'),
              button: l('Shop now', 'تسوّق الآن'),
              link: { kind: 'page', page: 'products' },
            },
          ],
        },
        {
          id: 'features',
          type: 'features',
          variant: 'row',
          items: [
            { id: 'f1', icon: '🚚', title: l('Fast delivery', 'توصيل سريع') },
            { id: 'f2', icon: '💳', title: l('Pay your way', 'ادفع بطريقتك') },
            { id: 'f3', icon: '⭐', title: l('Quality you trust', 'جودة مضمونة') },
          ],
        },
        {
          id: 'categories',
          type: 'categories',
          variant: 'circles',
          title: l('Shop by category', 'تسوّق حسب التصنيف'),
        },
        {
          id: 'featured',
          type: 'products',
          variant: 'carousel',
          source: 'featured',
          title: l('Featured', 'مختارات'),
        },
        {
          id: 'all',
          type: 'products',
          variant: 'grid',
          source: 'all',
          limit: 8,
          title: l('All products', 'كل المنتجات'),
        },
      ],
      products: [{ id: 'catalog', type: 'catalog' }],
      categories: [{ id: 'categories', type: 'categories', variant: 'tiles' }],
    },
  });
}

/** A new section of a type with its defaults (the console's "Add section"). */
export function newSection(type: SectionType, sectionId: string): DesignSection {
  return sectionSchema.parse({ id: sectionId, type });
}

// ------------------------------------------------------------- ready-made sections
/**
 * Sections filled in for a purpose (ADR-0128 amendment 3). Their words are neutral starting
 * points in both languages; reviews, answers and logos stay empty until the merchant adds real
 * ones, and an offer has no end until the merchant sets it (until then it does not show).
 */
export const READY_SECTIONS = [
  'heroSlider',
  'offerCountdown',
  'collage',
  'newArrivals',
  'featured',
  'bestSellers',
  'shopByCategory',
  'trustStrip',
  'reviews',
  'faq',
  'contactCta',
  'brands',
  'story',
  // ADR-0128 amendment 7.
  'ticker',
  'howItWorks',
  'aboutSplit',
  'highlights',
  'numbers',
] as const;
export type ReadySection = (typeof READY_SECTIONS)[number];
type SectionInput = z.input<typeof sectionSchema>;
/** A section's input without its id (spread over every section type). */
type SectionDraft = SectionInput extends infer T
  ? T extends unknown
    ? Omit<T, 'id'>
    : never
  : never;

const products = { kind: 'page', page: 'products' } as const;
const categoriesPage = { kind: 'page', page: 'categories' } as const;

function readyInput(name: ReadySection): SectionDraft {
  switch (name) {
    case 'heroSlider':
      return {
        type: 'slider',
        variant: 'contained',
        height: 'large',
        slides: [
          {
            id: 'slide-1',
            title: l('New season', 'الموسم الجديد'),
            subtitle: l('Discover what is new in the store', 'اكتشف الجديد في متجرنا'),
            button: l('Shop now', 'تسوّق الآن'),
            link: products,
          },
          {
            id: 'slide-2',
            title: l('Shop by category', 'تسوّق حسب التصنيف'),
            subtitle: l('Find what you need faster', 'وصّل لطلبك أسرع'),
            button: l('Browse', 'تصفّح'),
            link: categoriesPage,
          },
        ],
      };
    case 'offerCountdown':
      return {
        type: 'countdown',
        title: l('Limited-time offer', 'عرض لفترة محدودة'),
        subtitle: l('It ends soon, do not miss it.', 'ينتهي قريبًا، لا يفوتك.'),
        button: l('Shop the offer', 'تسوّق العرض'),
        link: products,
      };
    case 'collage':
      return {
        type: 'collage',
        tiles: [
          {
            id: 'tile-1',
            title: l('New in', 'وصل حديثًا'),
            button: l('Shop', 'تسوّق'),
            link: products,
          },
          { id: 'tile-2', title: l('Our picks', 'اختياراتنا'), link: products },
          { id: 'tile-3', title: l('Gifts', 'هدايا'), link: categoriesPage },
        ],
      };
    case 'newArrivals':
      return {
        type: 'products',
        variant: 'carousel',
        source: 'all',
        limit: 12,
        title: l('New arrivals', 'وصل حديثًا'),
      };
    case 'featured':
      return {
        type: 'products',
        variant: 'grid',
        source: 'featured',
        showAdd: true,
        title: l('Picked for you', 'مختارات لك'),
      };
    case 'bestSellers':
      return {
        type: 'products',
        variant: 'carousel',
        source: 'bestSellers',
        limit: 6,
        showAdd: true,
        title: l('Best sellers', 'الأكثر مبيعًا'),
      };
    case 'shopByCategory':
      return {
        type: 'categories',
        variant: 'circles',
        title: l('Shop by category', 'تسوّق حسب التصنيف'),
      };
    case 'trustStrip':
      return {
        type: 'features',
        variant: 'row',
        items: [
          {
            id: 'f1',
            icon: '🛍️',
            title: l('Order online', 'اطلب أونلاين'),
            text: l('Pickup or delivery', 'استلام أو توصيل'),
          },
          { id: 'f2', icon: '💳', title: l('Pay your way', 'ادفع بطريقتك') },
          { id: 'f3', icon: '💬', title: l('We are here to help', 'جاهزين نساعدك') },
        ],
      };
    case 'reviews':
      return { type: 'testimonials', title: l('What our customers say', 'آراء عملائنا') };
    case 'faq':
      return {
        type: 'faq',
        title: l('Questions and answers', 'أسئلة وأجوبة'),
        items: [
          { id: 'q1', question: l('How long does delivery take?', 'كم يستغرق التوصيل؟') },
          {
            id: 'q2',
            question: l('Which payment methods do you accept?', 'ما طرق الدفع المتاحة؟'),
          },
          { id: 'q3', question: l('Can I pick up my order?', 'هل أقدر أستلم طلبي بنفسي؟') },
          { id: 'q4', question: l('How do I return an item?', 'كيف أرجّع منتجًا؟') },
        ],
      };
    case 'contactCta':
      return {
        type: 'cta',
        variant: 'brand',
        title: l('Have a question?', 'عندك سؤال؟'),
        text: l('We are one message away.', 'راسلنا ونرد عليك.'),
        button: l('Message us', 'راسلنا'),
        button2: l('Shop now', 'تسوّق الآن'),
        link2: products,
      };
    case 'brands':
      return { type: 'logos', title: l('Brands we carry', 'الماركات المتوفرة') };
    case 'story':
      return { type: 'text', variant: 'card', align: 'center', title: l('Our story', 'قصتنا') };
    case 'ticker':
      return {
        type: 'marquee',
        variant: 'brand',
        items: [
          { id: 'm1', text: l('New arrivals', 'وصل حديثًا') },
          { id: 'm2', text: l('Order online', 'اطلب أونلاين') },
          { id: 'm3', text: l('Our picks for you', 'مختاراتنا لك') },
        ],
      };
    case 'howItWorks':
      return {
        type: 'steps',
        variant: 'numbers',
        title: l('How it works', 'كيف تطلب'),
        look: { titleAlign: 'center' },
        items: [
          { id: 's1', icon: '🛍️', title: l('Choose your products', 'اختر منتجاتك') },
          { id: 's2', icon: '💳', title: l('Pay your way', 'ادفع بطريقتك') },
          { id: 's3', icon: '📦', title: l('Receive your order', 'استلم طلبك') },
        ],
      };
    case 'aboutSplit':
      return {
        type: 'split',
        eyebrow: l('About us', 'من نحن'),
        title: l('Our story', 'قصتنا'),
        button: l('Shop now', 'تسوّق الآن'),
        link: products,
      };
    case 'highlights':
      return {
        type: 'cards',
        variant: 'overlay',
        aspect: 'portrait',
        hover: 'zoom',
        items: [
          { id: 'c1', title: l('New in', 'وصل حديثًا'), link: products },
          { id: 'c2', title: l('Our picks', 'اختياراتنا'), link: products },
          { id: 'c3', title: l('Categories', 'التصنيفات'), link: categoriesPage },
        ],
      };
    case 'numbers':
      return {
        type: 'stats',
        variant: 'row',
        look: { titleAlign: 'center' },
        items: [
          { id: 'n1', suffix: '+', label: l('Happy customers', 'عميل سعيد') },
          { id: 'n2', suffix: '+', label: l('Products', 'منتج') },
          { id: 'n3', label: l('Years with you', 'سنوات معكم') },
        ],
      };
  }
}

/** A ready-made section with its own id. */
export function readySection(name: ReadySection, sectionId: string): DesignSection {
  return sectionSchema.parse({ ...readyInput(name), id: sectionId });
}

/** Whole home pages for a kind of business, built from ready-made sections. */
export const HOME_LAYOUTS = [
  'fashion',
  'electronics',
  'grocery',
  'beauty',
  'restaurant',
  'cafe',
  'minimal',
] as const;
export type HomeLayout = (typeof HOME_LAYOUTS)[number];

export function homeLayout(layout: HomeLayout): DesignSection[] {
  const r = (name: ReadySection, extra: Record<string, unknown> = {}) =>
    sectionSchema.parse({ ...readyInput(name), ...extra, id: `${layout}-${name}` });
  const one = (sectionId: string, input: SectionDraft) =>
    sectionSchema.parse({ ...input, id: `${layout}-${sectionId}` });
  switch (layout) {
    case 'fashion':
      return [
        r('heroSlider', { variant: 'full' }),
        r('collage'),
        r('newArrivals'),
        r('shopByCategory', { variant: 'tiles' }),
        r('offerCountdown'),
        r('featured', { columns: 4 }),
        r('reviews'),
        r('trustStrip', { look: { background: 'soft' } }),
      ];
    case 'electronics':
      return [
        r('heroSlider'),
        r('trustStrip'),
        r('shopByCategory', { variant: 'chips' }),
        r('featured', { columns: 4 }),
        r('offerCountdown', { variant: 'card' }),
        r('brands'),
        r('newArrivals'),
        r('faq'),
      ];
    case 'grocery':
      return [
        one('hero', {
          type: 'banner',
          variant: 'color',
          imageSource: 'none',
          title: l('Your daily essentials', 'احتياجاتك اليومية'),
          subtitle: l(
            'Order in minutes, pick up or get it delivered.',
            'اطلب في دقائق، استلام أو توصيل.',
          ),
          button: l('Start shopping', 'ابدأ التسوق'),
          link: products,
        }),
        r('shopByCategory'),
        r('featured', { variant: 'carousel', showAdd: true }),
        r('collage', { variant: 'even' }),
        one('all', {
          type: 'products',
          source: 'all',
          limit: 12,
          showAdd: true,
          title: l('All products', 'كل المنتجات'),
        }),
        r('trustStrip'),
        r('faq'),
      ];
    case 'beauty':
      return [
        r('heroSlider', { variant: 'full', textAlign: 'center' }),
        r('shopByCategory'),
        r('featured', { columns: 3 }),
        r('story', { look: { background: 'tinted' } }),
        r('reviews', { variant: 'carousel' }),
        r('collage'),
        r('contactCta'),
      ];
    case 'restaurant':
      return [
        one('hero', {
          type: 'banner',
          variant: 'overlay',
          imageSource: 'store',
          align: 'center',
          height: 'large',
          title: l('Fresh from our kitchen', 'طازج من مطبخنا'),
          subtitle: l('Order for pickup or delivery', 'اطلب استلام أو توصيل'),
          button: l('Order now', 'اطلب الآن'),
          link: products,
        }),
        one('info', { type: 'info', variant: 'strip' }),
        r('featured', { variant: 'carousel', title: l('Most ordered', 'الأكثر طلبًا') }),
        one('menu', {
          type: 'menu',
          variant: 'delivery',
          quickAdd: true,
          cartBar: true,
          title: l('Menu', 'المنيو'),
        }),
        r('reviews'),
        r('contactCta', {
          title: l('Planning an event?', 'عندك مناسبة؟'),
          text: l('Message us for big orders.', 'راسلنا للطلبات الكبيرة.'),
        }),
      ];
    case 'cafe':
      return [
        one('hero', {
          type: 'banner',
          variant: 'split',
          imageSource: 'store',
          title: l('Your daily cup', 'كوبك اليومي'),
          subtitle: l('Order ahead and skip the line.', 'اطلب مسبقًا وتجنّب الانتظار.'),
          button: l('Order now', 'اطلب الآن'),
          link: products,
        }),
        one('info', { type: 'info', variant: 'cards', showBranches: true }),
        r('collage', { variant: 'even' }),
        one('menu', { type: 'menu', variant: 'compact', title: l('Menu', 'المنيو') }),
        one('gallery', { type: 'gallery', variant: 'mosaic', title: l('Moments', 'لحظات') }),
        r('faq'),
      ];
    case 'minimal':
      return [
        one('hero', {
          type: 'banner',
          variant: 'color',
          imageSource: 'none',
          align: 'center',
          title: l('Welcome', 'أهلًا بك'),
          button: l('Shop now', 'تسوّق الآن'),
          link: products,
        }),
        one('all', {
          type: 'products',
          source: 'all',
          limit: 12,
          columns: 3,
          title: l('Products', 'المنتجات'),
        }),
        r('story'),
      ];
  }
}

// -------------------------------------------------- section styles and categories page
/**
 * Ready styles per section type (ADR-0128 amendment 6): one tap sets how a section looks —
 * its variant, arrangement options and part of its frame — and never touches its content
 * (words, pictures, items, links, chosen products or categories). A style without its own
 * background or heading alignment resets them, so styles do not pile up.
 */
type StylePatch = Record<string, unknown> & {
  look?: { background?: string; titleAlign?: string };
};
const SECTION_STYLES = {
  slider: {
    classic: { variant: 'contained', height: 'medium', overlay: 'dark', textAlign: 'start' },
    bold: { variant: 'full', height: 'large', overlay: 'dark', textAlign: 'center' },
    cards: { variant: 'cards', height: 'small', overlay: 'light', textAlign: 'start' },
    soft: {
      variant: 'contained',
      height: 'small',
      overlay: 'light',
      textAlign: 'center',
      look: { background: 'soft' },
    },
  },
  banner: {
    hero: { variant: 'overlay', height: 'large', overlay: 'dark', align: 'center' },
    classic: { variant: 'overlay', height: 'medium', overlay: 'dark', align: 'start' },
    split: { variant: 'split', height: 'medium', align: 'start' },
    color: { variant: 'color', height: 'small', align: 'center' },
    glass: {
      variant: 'glass',
      height: 'large',
      overlay: 'medium',
      align: 'center',
      position: 'center',
    },
    fullscreen: {
      variant: 'overlay',
      height: 'screen',
      overlay: 'gradient',
      align: 'center',
      position: 'center',
      titleSize: 'huge',
    },
  },
  products: {
    grid: { variant: 'grid', columns: 4, showAdd: false, cardStyle: 'default', buyButton: 'none' },
    swipe: { variant: 'carousel', showAdd: true },
    list: { variant: 'list', showAdd: true },
    spotlight: {
      variant: 'grid',
      columns: 3,
      showAdd: true,
      look: { background: 'tinted', titleAlign: 'center' },
    },
    minimal: {
      variant: 'grid',
      columns: 4,
      showAdd: false,
      cardStyle: 'minimal',
      buyButton: 'none',
    },
    // Like the shops the owner showed (2026-10-22): a framed row with full-width buttons…
    shop: {
      variant: 'grid',
      columns: 6,
      showAdd: false,
      cardStyle: 'divided',
      imageFit: 'contain',
      showCategory: true,
      nameLines: '2',
      priceSize: 'large',
      buyButton: 'full',
      soldOut: 'corner',
      showViewAll: true,
    },
    // …and clean cards on white with a small button beside the price.
    clean: {
      variant: 'grid',
      columns: 5,
      showAdd: false,
      cardStyle: 'minimal',
      imageFit: 'contain',
      showCategory: true,
      nameLines: '1',
      buyButton: 'inline',
      soldOut: 'corner',
      look: { titleAlign: 'center' },
    },
  },
  categories: {
    circles: { variant: 'circles', showCount: false },
    tiles: { variant: 'tiles', columns: 4, aspect: 'landscape', showCount: false },
    cards: { variant: 'cards', columns: 4, aspect: 'square', showCount: true },
    banners: { variant: 'banners', showCount: true },
    chips: { variant: 'chips', showCount: false },
    list: { variant: 'list', showCount: true },
    carousel: { variant: 'carousel', showCount: false },
  },
  image: {
    wide: { variant: 'contained', aspect: 'wide' },
    full: { variant: 'full', aspect: 'wide' },
    square: { variant: 'contained', aspect: 'square' },
  },
  video: {
    classic: { variant: 'contained', aspect: 'landscape' },
    full: { variant: 'full', aspect: 'landscape' },
    reel: { variant: 'contained', aspect: 'portrait' },
  },
  text: {
    plain: { variant: 'plain', align: 'start' },
    centered: { variant: 'plain', align: 'center', look: { titleAlign: 'center' } },
    card: { variant: 'card', align: 'start' },
    highlight: { variant: 'highlight', align: 'center' },
  },
  features: {
    row: { variant: 'row' },
    grid: { variant: 'grid' },
    cards: { variant: 'cards', look: { background: 'soft', titleAlign: 'center' } },
    list: { variant: 'list' },
    bubbles: { variant: 'grid', iconStyle: 'square', look: { titleAlign: 'center' } },
  },
  menu: {
    list: { variant: 'list', showImages: true, showDescriptions: true },
    grid: { variant: 'grid', showImages: true, showDescriptions: false },
    compact: { variant: 'compact', showImages: false, showDescriptions: false },
    delivery: {
      variant: 'delivery',
      showImages: true,
      showDescriptions: true,
      quickAdd: true,
      cartBar: true,
    },
  },
  info: { strip: { variant: 'strip' }, cards: { variant: 'cards' } },
  catalog: {
    grid: { variant: 'grid', columns: 4, cardStyle: 'default', filterStyle: 'chips' },
    roomy: { variant: 'grid', columns: 3 },
    list: { variant: 'list' },
    modern: {
      variant: 'grid',
      columns: 4,
      cardStyle: 'soft',
      filterStyle: 'tabs',
      stickyFilters: true,
    },
  },
  countdown: {
    banner: { variant: 'banner' },
    card: { variant: 'card' },
    minimal: { variant: 'minimal' },
  },
  testimonials: {
    cards: { variant: 'cards' },
    swipe: { variant: 'carousel' },
    quote: { variant: 'quote', look: { background: 'soft', titleAlign: 'center' } },
  },
  faq: {
    accordion: { variant: 'accordion' },
    columns: { variant: 'columns' },
    centered: { variant: 'accordion', look: { background: 'soft', titleAlign: 'center' } },
  },
  gallery: {
    grid: { variant: 'grid', columns: 3 },
    mosaic: { variant: 'mosaic' },
    swipe: { variant: 'carousel' },
    masonry: { variant: 'masonry', columns: 3 },
  },
  logos: { row: { variant: 'row', grayscale: true }, grid: { variant: 'grid', grayscale: false } },
  collage: { mosaic: { variant: 'mosaic' }, even: { variant: 'even' } },
  cta: {
    brand: { variant: 'brand', align: 'center' },
    soft: { variant: 'soft', align: 'center' },
    outline: { variant: 'outline', align: 'start' },
    picture: { variant: 'image', align: 'center', buttonStyle: 'solid', size: 'regular' },
    inline: { variant: 'brand', align: 'start', layout: 'inline' },
    glass: { variant: 'image', align: 'center', buttonStyle: 'glass', size: 'large' },
  },
  spacer: {
    small: { size: 'small', line: false },
    line: { size: 'medium', line: true },
    large: { size: 'large', line: false },
  },
  divider: {
    line: { variant: 'line', length: 'full', color: 'border' },
    dots: { variant: 'dots', length: 'short', color: 'brand' },
    gradient: { variant: 'gradient', length: 'half', color: 'brand' },
    wave: { variant: 'wave', length: 'short', color: 'brand' },
  },
  columns: {
    two: { layout: '1-1', columnStyle: 'plain', dividers: false },
    cards: { layout: '1-1-1', columnStyle: 'card', dividers: false },
    split: { layout: '2-1', columnStyle: 'plain', dividers: true },
    glass: {
      layout: '1-1',
      columnStyle: 'glass',
      dividers: false,
      look: { background: 'gradient' },
    },
  },
  split: {
    classic: { imagePosition: 'start', ratio: 'even', imageShape: 'rounded' },
    reverse: { imagePosition: 'end', ratio: 'even', imageShape: 'rounded' },
    arch: { imagePosition: 'start', ratio: 'even', imageShape: 'arch' },
    circle: { imagePosition: 'end', ratio: 'text', imageShape: 'circle' },
    wide: { imagePosition: 'start', ratio: 'image', imageShape: 'rect' },
  },
  cards: {
    bordered: { variant: 'bordered', hover: 'lift' },
    shadow: { variant: 'shadow', hover: 'lift' },
    overlay: { variant: 'overlay', hover: 'zoom', aspect: 'portrait' },
    glass: { variant: 'glass', hover: 'glow', look: { background: 'gradient' } },
  },
  tabs: {
    pills: { variant: 'pills' },
    underline: { variant: 'underline' },
    boxed: { variant: 'boxed', look: { background: 'soft' } },
    vertical: { variant: 'vertical' },
  },
  marquee: {
    brand: { variant: 'brand', size: 'regular' },
    dark: { variant: 'dark', size: 'regular' },
    outline: { variant: 'outline', size: 'large' },
    tilted: { variant: 'tilted', size: 'regular' },
  },
  stats: {
    row: { variant: 'row' },
    cards: { variant: 'cards' },
    boxed: { variant: 'boxed', look: { background: 'brand', titleAlign: 'center' } },
  },
  steps: {
    numbers: { variant: 'numbers' },
    timeline: { variant: 'timeline' },
    cards: { variant: 'cards', look: { background: 'soft', titleAlign: 'center' } },
  },
} as const satisfies Record<SectionType, Record<string, StylePatch>>;

/** The ready styles of a section type, in the order the console offers them. */
export function sectionStyles(type: SectionType): string[] {
  return Object.keys(SECTION_STYLES[type]);
}

function stylePatch(type: SectionType, style: string): StylePatch | null {
  return (SECTION_STYLES[type] as Record<string, StylePatch>)[style] ?? null;
}

/** The section with a ready style applied; its content is kept. Unknown styles change nothing. */
export function applySectionStyle(section: DesignSection, style: string): DesignSection {
  const patch = stylePatch(section.type, style);
  if (!patch) return section;
  const { look, ...rest } = patch;
  return sectionSchema.parse({
    ...section,
    ...rest,
    look: { ...section.look, background: 'none', titleAlign: 'start', ...look },
  });
}

/** The ready style a section matches now, if any. */
export function currentSectionStyle(section: DesignSection): string | null {
  const record = section as unknown as Record<string, unknown>;
  for (const style of sectionStyles(section.type)) {
    const { look, ...rest } = stylePatch(section.type, style)!;
    const wanted = { background: 'none', titleAlign: 'start', ...look };
    if (
      Object.entries(rest).every(([k, v]) => record[k] === v) &&
      section.look.background === wanted.background &&
      section.look.titleAlign === wanted.titleAlign
    )
      return style;
  }
  return null;
}

/** Whole categories pages offered in the console (ADR-0128 amendment 6). */
export const CATEGORY_LAYOUTS = ['tiles', 'circles', 'cards', 'list', 'showcase'] as const;
export type CategoryLayout = (typeof CATEGORY_LAYOUTS)[number];

export function categoryLayout(layout: CategoryLayout): DesignSection[] {
  const r = (name: ReadySection, extra: Record<string, unknown> = {}) =>
    sectionSchema.parse({ ...readyInput(name), ...extra, id: `cat-${layout}-${name}` });
  const one = (sectionId: string, input: SectionDraft) =>
    sectionSchema.parse({ ...input, id: `cat-${layout}-${sectionId}` });
  const categories = (extra: Record<string, unknown>) =>
    one('categories', { type: 'categories', title: l('Categories', 'التصنيفات'), ...extra });
  switch (layout) {
    case 'tiles':
      return [categories({ variant: 'tiles', columns: 3 }), r('featured', { columns: 4 })];
    case 'circles':
      return [
        categories({ variant: 'circles', topOnly: true }),
        r('newArrivals'),
        r('featured', { variant: 'grid', columns: 4 }),
      ];
    case 'cards':
      return [
        categories({
          variant: 'cards',
          columns: 4,
          aspect: 'square',
          showCount: true,
          showSub: true,
        }),
        r('trustStrip', { look: { background: 'soft' } }),
      ];
    case 'list':
      return [
        categories({ variant: 'list', showCount: true, showSub: true, topOnly: true }),
        r('contactCta'),
      ];
    case 'showcase':
      return [
        r('collage'),
        categories({ variant: 'banners', showCount: true, topOnly: true }),
        r('featured'),
      ];
  }
}

// ------------------------------------------------------------------------------- API
// Explicit types: the design schema is too large for TypeScript to write out inline.
type DesignSchema = typeof storeDesignSchema;
export const storeDesignStateSchema: z.ZodObject<{
  draft: DesignSchema;
  published: z.ZodNullable<DesignSchema>;
  publishedAt: z.ZodNullable<z.ZodString>;
  dirty: z.ZodBoolean;
  suggestedTemplate: z.ZodEnum<{ retail: 'retail'; restaurant: 'restaurant' }>;
  version: z.ZodNumber;
}> = z.object({
  draft: storeDesignSchema,
  published: storeDesignSchema.nullable(),
  publishedAt: z.string().nullable(),
  /** Unpublished changes exist. */
  dirty: z.boolean(),
  /** The template suggested for the company (restaurant module on = restaurant). */
  suggestedTemplate: z.enum(DESIGN_TEMPLATES),
  version: z.number().int(),
});
export type StoreDesignState = z.infer<typeof storeDesignStateSchema>;
export const saveStoreDesignSchema: z.ZodObject<{
  design: DesignSchema;
  version: z.ZodNumber;
}> = z.object({
  design: storeDesignSchema,
  version: z.number().int().nonnegative(),
});
export const storeDesignVersionSchema = z.object({ version: z.number().int().nonnegative() });

export const storeAssetSchema = z.object({
  id: z.string(),
  url: z.string(),
  contentType: z.string(),
  bytes: z.number().int(),
  createdAt: z.string(),
});
export type StoreAsset = z.infer<typeof storeAssetSchema>;
export const storeAssetListSchema = z.object({ assets: z.array(storeAssetSchema) });

/** What the storefront reads: the published design (or the legacy layout). */
export const publicStoreDesignSchema: z.ZodObject<{ design: DesignSchema }> = z.object({
  design: storeDesignSchema,
});
